// Home page texts in English. In "rich" texts the <b> tag is rendered bold, <link> as a link.
const home = {
  nav: {
    home: "Home",
    services: "Services",
    qa: "Quality Assurance",
    products: "Products",
    references: "References",
    about: "About us",
    workWithUs: "Work with us",
    contact: "Contact us",
    openMenu: "Open menu",
    closeMenu: "Close menu",
    menu: "Menu",
  },

  hero: {
    text: "An AI-augmented software house, governed by Quality Assurance. We build faster, controlled and scalable delivery.",
    ctaServices: "Explore our services",
    ctaTalk: "Talk to us",
  },

  partners: {
    label: "THEY CHOSE QAPEX",
    aria: "Partners",
  },

  model: {
    eyebrow: "Our model",
    title: "More than software development.",
    lead: "AI increases speed. The competitive edge comes when that speed stays <b>governed</b>. We bring AI pipelines, development and Quality Assurance together in a single operating flow.",
    agentic: {
      title: "AGENTIC ENGINEERING",
      text: "AI and automation pipelines that speed up analysis, development and documentation on projects of any size, cloud included.",
    },
    qa: {
      title: "QUALITY ASSURANCE",
      text: "Structured QA built into every phase: control, method and reliability of both process and product. AI doesn't replace engineering: it amplifies it.",
    },
  },

  services: {
    eyebrow: "Our services",
    title: "Modular services, built-in quality.",
    lead: "To accelerate digital roadmaps, automation and cloud-native applications.",
    items: [
      { title: "Cloud DevOps", text: "CI/CD pipelines, infrastructure as code and scalable, monitored and secure cloud environments." },
      { title: "Business AI Pipelines", text: "Smart automation that speeds up analysis, documentation and business processes." },
      { title: "Local AI & Custom Models", text: "Private or on-premise AI models, trained on your data without exposing it." },
      { title: "Cloud-Native Web & Mobile", text: "Websites, e-commerce and apps designed to scale: fast, usable and conversion-driven." },
      { title: "Optimization & Performance", text: "Analysis, load testing and tuning for faster, more stable and reliable applications." },
      { title: "AI Code Assurance", text: "We verify AI-generated code: review, testing and governance before every release." },
    ],
  },

  qa: {
    eyebrow: "Quality Assurance · Born for test automation",
    title: "Quality is",
    titleHighlight: "not accidental.",
    lead: "QA doesn't slow delivery down: it makes it sustainable. ISTQB-certified teams, proprietary AI and automation in every release cycle.",
    cards: [
      {
        tag: "END-TO-END",
        title: "QA Service Provider",
        text: "The core of our offering: end-to-end, scalable test management. Tailored test books with proprietary AI, functional, system and integration test cycles, regressions tracked on every push.",
        chips: ["End-to-end management", "Guaranteed scalability", "Continuous quality"],
      },
      {
        tag: "PREMIUM",
        title: "QA Consulting",
        text: "Consultants with corporate experience and cross-functional skills: \"dualists\" who master both manual testing and automation, with AI built into their daily work.",
        chips: ["Certified expertise", "Immediate adaptability", "Hybrid skills"],
      },
    ],
    cert: {
      eyebrow: "Qapex Certification",
      title: "Test with Qapex.",
      titleLine2: "Release with our certificate.",
      discover: "Find out how to get it",
      text: "If you choose Qapex to test your software, at the end of the cycle we issue the <b>Qapex Quality Assured Certification</b>: an internal certificate documenting coverage, results and release quality, to share with clients, partners and stakeholders.",
      certTitle: "QUALITY CERTIFICATE",
      project: "Project: Application name · release 2.4",
      kpis: ["Tests run", "Coverage", "Critical bugs"],
      date: "10/12/2026",
      dateLabel: "Issue date",
    },
  },

  // Pagina /certificazione-qapex (titolo, testo ed esempio di certificato sono in qa.cert)
  certPage: {
    howEyebrow: "How to get it",
    howTitle: "Four steps, from requirements to certificate.",
    howLead: "The certificate is the result of a complete test cycle, run by our ISTQB-certified team.",
    steps: [
      { title: "Requirements analysis & Testbook AI", text: "We analyse your software's requirements and, with our proprietary AI, prepare a tailored test book: the test cases to run." },
      { title: "Test cycle execution", text: "We run functional, system and integration test cycles, with regressions tracked on every release." },
      { title: "Quality and traceability report", text: "We gather results, coverage and defects in a quality report, with traceability between requirements and tests." },
      { title: "Certificate issued", text: "At the end of the cycle we issue the Qapex Quality Assured Certification, ready to share with clients, partners and stakeholders." },
    ],
    ctaTitle: "Want to certify your next release?",
    ctaText: "Tell us about your project and we'll propose the right test cycle. The first consultation is free.",
    cta: "Request a test cycle",
  },

  process: {
    eyebrow: "How we work",
    title: "From idea to release, with QA in every sprint.",
    lead: "Short cycles, continuous validation and built-in quality. Technical roadmap and QA plan stay in sync: one defines what gets built, the other how it gets verified.",
    steps: [
      { phase: "KICKOFF", title: "Clear goals", text: "Defining goals and success criteria, and aligning on tech stack and constraints." },
      { phase: "SPRINT CYCLES", title: "Verified development", text: "Development with technical reviews and QA checkpoints in every sprint, not just at the end." },
      { phase: "GO-LIVE", title: "Safe release", text: "Quality checklist, monitoring plan and continuous improvement roadmap." },
    ],
  },

  products: {
    eyebrow: "Our products",
    title: "Proprietary AI tools.",
    demo: "Request a demo",
    quack: {
      text: "Upload audio, video, notes or whiteboard photos: get functional analysis, backlog and change requests ready as .docx and .pdf.",
      cta: "Discover QUACK",
      play: "Watch the QUACK presentation video",
      tools: "Tools",
      steps: ["Meeting", "Backlog", "Change Request"],
      running: "Analysis in progress…",
    },
    subq: {
      text: "Work in progress",
      cta: "Discover SubQ",
    },
  },

  certs: {
    eyebrow: "Certifications",
    title: "International, verifiable standards.",
    lead: "Our team is certified to the reference standards for software quality.",
    active: "✓ ACTIVE · 2026",
    planned: "PLANNED · 2026–2028",
    istqb: "International Software Testing Qualifications Board, recognized in more than 120 countries.",
    iso9001: "Quality management system for business processes.",
    iso27001: "Information security and data protection.",
  },

  refs: {
    eyebrow: "References",
    title: "What they say about us.",
    carousel: "carousel",
    goTo: "Review {n}",
    items: {
      stefanetti: {
        role: "Founder, Symposium Group",
        text: "We chose Qapex to develop our community's app and we have been really happy with them: a young team eager to get things done, attentive to quality, to detail and above all to speed. I recommend them with all my heart.",
      },
      pettini: {
        role: "CTO, Engineer, Teacher",
        text: "Nima and the team took charge of every aspect of testing for the entire ParkingMyCar service, introduced testing methodologies and built a test automation framework, ensuring quality in every one of our releases. It was an excellent investment.",
      },
      innamorati: {
        role: "Connecting the finest SAP Professionals worldwide",
        text: "Qapex has consultants that everyone would love to work with. They are very competent other than fully committed and humble. They have promptly resolved many issues related to the testing phase of various projects.",
      },
      picciotto: {
        role: "QA Specialist, Competence Center & Offering Management",
        text: "Always available and ready to step up. When there is a project to deliver, I know I can count on Nima and the team to deliver it with quality.",
      },
      lombardozzi: {
        role: "Program Manager",
        text: "A professional with strong problem-solving skills, great soft skills and excellent communication. Their ideas have always been challenging, raising the quality and efficiency of every project.",
      },
    },
  },

  contact: {
    eyebrow: "Contact",
    title: "Ready to transform your business?",
    lead: "Let's talk. Our team is ready to build tailored digital solutions with you. The first consultation is free.",
    email: "Email",
    office: "Office",
    officeValue: "Via della Louvière 1/A, 06034 Foligno (PG), Italy",
    vat: "VAT",
    formTitle: "Write to us",
    firstName: "First name",
    firstNamePlaceholder: "John",
    lastName: "Last name",
    lastNamePlaceholder: "Smith",
    emailPlaceholder: "john@company.com",
    phone: "Phone number",
    phonePrefix: "Prefix",
    phonePlaceholder: "333 123 4567",
    service: "Service of interest",
    selectService: "Select an option",
    message: "Message",
    messagePlaceholder: "How can we help you?",
  },

  footer: {
    tagline: "We build software with the speed of AI and the rigor of QA. From Foligno, in the green heart of Umbria.",
    navigate: "NAVIGATE",
    company: "COMPANY",
    legal: "LEGAL",
    contacts: "Contact",
    privacy: "Privacy Policy",
    cookies: "Cookie Policy",
    legalNotes: "Legal notes",
    rights: "QAPEX S.r.l. · VAT IT03818350542 · All rights reserved.",
  },

  video: {
    dialog: "Video",
    close: "Close video",
  },
};

export default home;
